// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement } from 'react';
import { PageInput } from 'thehive-sdk';
import { classList } from '@Helpers/className';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';

import './KnowledgeBaseCreate.scss';

interface KnowledgeBaseFormValues {
    title: string;
    category: string;
}

interface KnowledgeBaseCreateProps {
    onCreate: (page: PageInput) => void;
    allCategories?: string[];
    triggerElem?: ReactElement;
}

export const KnowledgeBaseCreate = ({
    onCreate,
    allCategories = [],
    triggerElem,
}: KnowledgeBaseCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: KnowledgeBaseFormValues): void => {
        const { title = '', category = '' } = values || {};

        onCreate({ title, content: '', category });
        setFieldsTouched(false);
    };

    return (
        <THDrawer
            title={i('page.create.drawer.knowledge-base')}
            triggerElem={triggerElem}
            className={classList('knowledge-base-create', 'drawer-form')}
            destroyOnClose
            disableClose={fieldsTouched}
            afterVisibleChange={(): void => {
                form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <THForm<KnowledgeBaseFormValues>
                    form={form}
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                    toggleDrawer();
                                }}
                                disabled={!isValid || !fieldsTouched}
                                footerText={i('general.confirm')}
                            />
                        }
                    >
                        <THForm.Item
                            name="title"
                            label={i('page.create.form.title.label')}
                            rules={[{ required: true, message: i('page.create.form.title.required') }]}
                        >
                            <THInput placeholder={i('page.create.form.title.placeholder')} />
                        </THForm.Item>

                        <THForm.Item
                            name="category"
                            label={i('page.create.form.category.label')}
                            rules={[{ required: true, message: i('page.create.form.category.required') }]}
                        >
                            <THAutoComplete
                                placeholder={i('page.create.form.category.placeholder')}
                                options={allCategories.map((value) => ({ label: value, value }))}
                            />
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
